import React, { useCallback, useEffect, useState } from 'react'
import _, { debounce } from 'lodash'
import { connect, FormikContextType, FormikErrors } from 'formik'
import { TemplateDetailSchema } from './TemplateDetail'
import { TemplateFilterSchema } from './TemplateSelector'

type IntersectionSchema = TemplateDetailSchema & TemplateFilterSchema

type SkipCallbackError = {
    [key in keyof TemplateDetailSchema]?: string[]
} & {
    [key in keyof TemplateFilterSchema]?: string[]
}
export interface OnFormikValueChangeProps {
    callback: (values: IntersectionSchema) => void
    fields: (keyof TemplateDetailSchema)[] | (keyof TemplateFilterSchema)[]
    skipCallbackError: SkipCallbackError
    validateSpecificField: (
        fieldNames: (keyof TemplateDetailSchema)[] | (keyof TemplateFilterSchema)[],
    ) => Promise<FormikErrors<IntersectionSchema>>
    showError?: boolean
    updateCurrentStepStatus?: (isCompleted: boolean, isError: boolean) => void
    setDisableNavigation?: React.Dispatch<React.SetStateAction<boolean>>
    disableAutoSave?: boolean // to skip validation and autosave on certain case
}

type Props = OnFormikValueChangeProps & { formik: FormikContextType<IntersectionSchema> }

const OnFormikValueChange: React.FC<Props> = ({
    callback,
    fields,
    skipCallbackError,
    validateSpecificField,
    showError = false,
    disableAutoSave = false,
    updateCurrentStepStatus,
    setDisableNavigation,
    formik,
}) => {
    const { values, setTouched } = formik
    const [currentValues, setCurrentValues] = useState<IntersectionSchema>(values)

    // debounce validation to prevent lag / performance issue while typing into input
    const debouncedValidation = useCallback(
        debounce(
            () => {
                const touchedObject = fields.reduce((acc, curr) => {
                    return {
                        ...acc,
                        [curr]: true,
                    }
                }, {})
                setTouched(touchedObject)
                validateSpecificField(fields).then(
                    (errors: FormikErrors<TemplateDetailSchema & TemplateFilterSchema>) => {
                        if (updateCurrentStepStatus) {
                            const isError = fields.some(field => (showError ? !_.isEmpty(errors[field]) : false))
                            const isCompleted = _.isEmpty(errors)
                            updateCurrentStepStatus(isCompleted, isError)
                        }
                        if (!_.isEqual(values, currentValues)) {
                            const fieldsValues = _.pick(
                                values,
                                fields.filter(
                                    field =>
                                        !(errors[field] && skipCallbackError[field]?.includes(errors[field] as string)),
                                ),
                            )
                            callback(fieldsValues)
                            setCurrentValues(values)
                        }
                    },
                )
            },

            // used 400 / 0
            // 0 is used for first time render
            // other than first time will have 1 second delay
            // if we use 400, setTimeout (200ms) in TemplateDetail will update sidebar incorrectly
            !_.isEqual(values, currentValues) ? 400 : 0,
        ),
        [values, currentValues, showError],
    )
    useEffect(() => {
        if (disableAutoSave) {
            return
        }
        // disable click flag only when change & not initial value
        if (!_.isEqual(values, currentValues)) {
            setDisableNavigation?.(true)
        }
        debouncedValidation()

        // cancel debounce on cleanup
        return () => debouncedValidation.cancel()
    }, [values, currentValues, showError])
    return null
}

export default connect<OnFormikValueChangeProps, IntersectionSchema>(OnFormikValueChange)
